import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForgetPasswordSubmit } from '../../hooks/useAuth';

const cardSx = {
    width: 648,
    maxWidth: '94vw',
    px: { xs: 2.5, md: 6 },
    py: { xs: 3, md: 4.5 },
    borderRadius: '28px',
    border: '1px solid rgba(214, 55, 49, 0.08)',
    bgcolor: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0px 24px 70px rgba(61, 61, 61, 0.10)',
    backdropFilter: 'blur(6px)'
};

const fieldSx = {
    '& .MuiInputLabel-root': {
        color: '#2E3442',
        fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
        bgcolor: '#FFFFFF',
        borderRadius: '16px',
        minHeight: 58,
        '& fieldset': {
            borderColor: '#D8DFEB'
        },
        '&:hover fieldset': {
            borderColor: '#C9D3E2'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D63731',
            borderWidth: '1px'
        }
    },
    '& .MuiOutlinedInput-input': {
        color: '#101828',
        fontSize: '1rem'
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 100px #FFFFFF inset',
        WebkitTextFillColor: '#101828',
        caretColor: '#101828',
        borderRadius: '12px',
        transition: 'background-color 9999s ease-out 0s'
    }
};

const primaryButtonSx = {
    minHeight: 54,
    width: '100%',
    textTransform: 'none',
    fontWeight: 800,
    fontSize: '1.06rem',
    borderRadius: '14px',
    bgcolor: '#E91C24',
    boxShadow: '0px 12px 24px rgba(233, 28, 36, 0.22)',
    '&:hover': {
        bgcolor: '#D6171F',
        boxShadow: '0px 12px 24px rgba(233, 28, 36, 0.24)'
    },
    '&.Mui-disabled': {
        bgcolor: 'rgba(233, 28, 36, 0.45)',
        color: '#FFFFFF'
    }
};

const ForgetPasswordSubmit = () => {
    const navigate = useNavigate();
    const { state, search } = useLocation();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const searchParams = new URLSearchParams(search || '');
    const emailFromQuery = searchParams.get('email') || '';
    const emailSeed = state?.email || emailFromQuery;

    const formik = useForgetPasswordSubmit(emailSeed);

    return (
        <Stack alignItems="center" spacing={2.5} sx={{ width: '100%' }}>
            <Paper sx={cardSx}>
                <Stack spacing={3}>
                    <Stack spacing={1.2} alignItems="center" textAlign="center">
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#D63731',
                                fontWeight: 900,
                                fontSize: { xs: '2rem', md: '3.15rem' },
                                lineHeight: 1,
                                letterSpacing: '0.01em'
                            }}
                        >
                            INFO.CUIDADOR 2.0
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: '#1F2937',
                                fontSize: { xs: '1.75rem', md: '2.15rem' }
                            }}
                        >
                            Definir nova palavra-passe
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#667085',
                                fontSize: { xs: '1rem', md: '1.1rem' }
                            }}
                        >
                            Introduza o código recebido e escolha a sua nova palavra-passe
                        </Typography>
                    </Stack>

                    <Box component="form" onSubmit={formik.handleSubmit}>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                autoComplete="username"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.email && formik.errors.email)}
                                helperText={
                                    formik.touched.email && formik.errors.email ? formik.errors.email : ' '
                                }
                                sx={fieldSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MailOutlineRoundedIcon sx={{ color: '#5F6C80', fontSize: 22 }} />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Código de validação"
                                name="validationCode"
                                autoComplete="one-time-code"
                                value={formik.values.validationCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(
                                    formik.touched.validationCode && formik.errors.validationCode
                                )}
                                helperText={
                                    formik.touched.validationCode && formik.errors.validationCode
                                        ? formik.errors.validationCode
                                        : ' '
                                }
                                sx={fieldSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <KeyRoundedIcon sx={{ color: '#5F6C80', fontSize: 22 }} />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Nova palavra-passe"
                                name="newPassword"
                                type={showNewPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={formik.values.newPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.newPassword && formik.errors.newPassword)}
                                helperText={
                                    formik.touched.newPassword && formik.errors.newPassword
                                        ? formik.errors.newPassword
                                        : ' '
                                }
                                sx={fieldSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockOutlinedIcon sx={{ color: '#5F6C80', fontSize: 22 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() => setShowNewPassword((prev) => !prev)}
                                                sx={{ color: '#5F6C80' }}
                                            >
                                                {showNewPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Confirmar palavra-passe"
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={formik.values.confirmPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(
                                    formik.touched.confirmPassword && formik.errors.confirmPassword
                                )}
                                helperText={
                                    formik.touched.confirmPassword && formik.errors.confirmPassword
                                        ? formik.errors.confirmPassword
                                        : ' '
                                }
                                sx={fieldSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockOutlinedIcon sx={{ color: '#5F6C80', fontSize: 22 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                sx={{ color: '#5F6C80' }}
                                            >
                                                {showConfirmPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {formik.errors.submit ? (
                                <Alert severity="error" sx={{ borderRadius: '16px', alignItems: 'center' }}>
                                    {formik.errors.submit}
                                </Alert>
                            ) : null}

                            <Button
                                variant="contained"
                                type="submit"
                                disabled={formik.isSubmitting}
                                sx={primaryButtonSx}
                            >
                                {formik.isSubmitting ? 'A alterar...' : 'Alterar palavra-passe'}
                            </Button>
                        </Stack>
                    </Box>

                    <Typography
                        variant="body1"
                        textAlign="center"
                        sx={{ color: '#667085', fontSize: { xs: '0.98rem', md: '1.05rem' } }}
                    >
                        Voltar atrás?{' '}
                        <Box
                            component="span"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationThickness: '1.5px',
                                textUnderlineOffset: '2px',
                                color: '#D63731',
                                fontWeight: 700
                            }}
                            onClick={() => navigate('/forgetPassword')}
                        >
                            Recuperar novamente
                        </Box>
                    </Typography>
                </Stack>
            </Paper>
        </Stack>
    );
};

export default ForgetPasswordSubmit;
